import React, { Fragment, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import InstructionalText from '../../components/ssap/notifications/InstructionalText';

import { DATE_FORMAT_DASHED } from '../../utils/dates/dateMethods';
import validators from '../../utils/validators/validators';
import { DIGIT_ONLY_REGEX } from '../../utils/validators/regex';
import {
    constructDate,
    convertDateToUtc,
    formatDate,
    getDateTimeInMillisecond,
    parseDate,
} from '../../utils/dates/date.utils';

import { VALIDATION_TYPES } from '../../constants/inputs/validationTypes';


const DatePicker = props => {

    const {
        inputData: {
            id,
            isInputRequired,
            name,
            value,
            divClass,
            specificValidation
        },
        indexInArray,
        label: {
            dataInstructions,
            dataInstructionsAnchor: anchor,
            replacements
        }
    } = props;

    const DATE = 'date';
    const MONTH = 'month';
    const YEAR = 'year';
    const intl = useIntl();
    const yearPlaceholder = intl.formatMessage({ id:'common.abbr.year' });

    const defaultDate = {
        [ DATE ]: '',
        [ MONTH ]: '',
        [ YEAR ]: ''
    };

    const [ fullDate, updateFullDate] = useState(defaultDate);

    const date = fullDate[DATE];
    const month = fullDate[MONTH];
    const year = fullDate[YEAR];

    useEffect(() => {
        if(value === DATE_FORMAT_DASHED) {
            return;
        }
        if (value === null || value === '') {
            updateFullDate(defaultDate);
        } else {
            let timeValue;

            switch (typeof value) {
                case 'string': // from user typing into DatePicker
                    timeValue = parseDate(value);
                    break;

                case 'number': // from anonymous shopping (old jsp)
                    timeValue = convertDateToUtc(value);
                    break;
            }

            updateFullDate({
                [ DATE ]: formatDate(timeValue, 'DD'),
                [ MONTH ]: formatDate(timeValue, 'MM'),
                [ YEAR ]: formatDate(timeValue, 'YYYY')
            });
        }
    }, [value]);

    const handleValueChange = e => {
        const { name } = e.target;
        const value = e.target.value.replace(DIGIT_ONLY_REGEX, '');
        const validator = validators.find(validator => validator.validatorType === VALIDATION_TYPES.DATEPICKER );

        if(validator.verifier(value)) {
            return null;
        }

        switch(name) {
            case MONTH:
                if ((+value > 12 || +value < 0) || value.length > 2) {
                    return null;
                }
                break;
            case DATE:
                if ((value > 31 || value < 0) || value.length > 2) {
                    return null;
                }
                break;
            case YEAR:
                if(value.length > 4) {
                    return null;
                }
                break;
        }

        updateFullDate(date => ({
            ...date,
            [ name ]: value
        }));
    };

    const handleBlur = () => {
        const newTimeStamp = constructDate({
            [ DATE ]: +date,
            [ MONTH ]: +month - 1,
            [ YEAR ]: +year
        });
        if (specificValidation && !Object.values(fullDate).every(v => v === '') && // if specificValidation exists AND fullDate is not completely empty
            (isNaN(getDateTimeInMillisecond(newTimeStamp)) || getDateTimeInMillisecond(newTimeStamp) < getDateTimeInMillisecond('1900-01-01')) // if newTimeStamp is NaN OR newTimeStamp is under minimum threshold
        ) {
            props.inputActions.handleFieldChange(DATE_FORMAT_DASHED);
        } else {
            props.inputActions.handleFieldChange(
                (!newTimeStamp || isNaN(newTimeStamp.toDate().getTime())
                    || newTimeStamp.toDate().getTime() < -62135654400000) ?
                    null : newTimeStamp.format(DATE_FORMAT_DASHED)
            );
        }
    };

    return (
        <Fragment>
            <div className="grid-row gi-component gi-datepicker">
                <div className="grid-col-12">
                    <fieldset className="usa-fieldset">
                        <legend className={`usa-legend gi-datepicker__label ${props.label.labelClass}`}>
                            <span className={`required-${isInputRequired}`}>{props.label.text}</span>
                            {
                                dataInstructions &&

                                <InstructionalText
                                    anchor = { anchor }
                                    Component = {
                                        <span data-instructions = { dataInstructions } />
                                    }
                                    replacements = { replacements }
                                />

                            }
                        </legend>

                        <span className={`grid-row usa-memorable-date ${divClass}`}>
                            <span className="usa-form-group usa-form-group--month gi-form-group_month txt-left">
                                <label htmlFor={`hhm${indexInArray}_${name}_1`}>
                                    <FormattedMessage id="common.month"/>
                                </label>
                                <input
                                    id={`hhm${indexInArray}_${name}_1`}
                                    data-testid = { `${ id }_month` }
                                    name={MONTH}
                                    maxLength={2}
                                    value={month}
                                    placeholder="MM"
                                    onChange={handleValueChange}
                                    onBlur={handleBlur}
                                    className="usa-input usa-input--inline gi-datepicker_input"
                                />
                            </span>
                            <span className="usa-form-group usa-form-group--day gi-form-group_day txt-left">
                                <label htmlFor={`hhm${indexInArray}_${name}_2`}>
                                    <FormattedMessage id="common.day"/>
                                </label>
                                <input
                                    id={`hhm${indexInArray}_${name}_2`}
                                    data-testid = { `${ id }_day` }
                                    name={DATE}
                                    maxLength={2}
                                    value={date}
                                    placeholder="DD"
                                    onChange={handleValueChange}
                                    onBlur={handleBlur}
                                    className="usa-input usa-input--inline gi-datepicker_input"
                                />
                            </span>
                            <span className="usa-form-group usa-form-group--year gi-form-group_year txt-left">
                                <label htmlFor={`hhm${indexInArray}_${name}_3`}>
                                    <FormattedMessage id="common.year"/>
                                </label>
                                <input
                                    id={`hhm${indexInArray}_${name}_3`}
                                    data-testid = { `${ id }_year` }
                                    name={YEAR}
                                    maxLength={4}
                                    value={year}
                                    placeholder={yearPlaceholder}
                                    onChange={handleValueChange}
                                    onBlur={handleBlur}
                                    className="usa-input usa-input--inline gi-datepicker_input"
                                />
                            </span>
                        </span>
                    </fieldset>
                </div>
            </div>


        </Fragment>
    );
};

export default DatePicker;
